/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import jwtDecode from 'jwt-decode';
import uuid from 'react-uuid';
const AUTH_TOKEN_KEY = 'authToken';
const PERSIST_KEY = 'persistAuthToken';
const SESSION_KEYS = 'sessionGuids';
const SESSION_KEY = 'sessionGuid';
const clearTokenFromStorage = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(PERSIST_KEY);
    localStorage.removeItem(SESSION_KEYS);
};
const addSessionKey = () => {
    const existingSessionGuid = sessionStorage.getItem(SESSION_KEY);
    const existingToken = localStorage.getItem(AUTH_TOKEN_KEY);
    const existingSessions = JSON.parse(localStorage.getItem(SESSION_KEYS) || '[]');
    if (!existingSessionGuid && existingToken) {
        const guid = uuid();
        sessionStorage.setItem(SESSION_KEY, guid);
        existingSessions.push(guid);
        localStorage.setItem(SESSION_KEYS, JSON.stringify(existingSessions));
    }
    else if (existingToken && !existingSessions.find((session) => session === existingSessionGuid)) {
        existingSessions.push(existingSessionGuid);
        localStorage.setItem(SESSION_KEYS, JSON.stringify(existingSessions));
    }
};
const store = createContext({ state: {}, setState: () => null });
const { Provider } = store;
const SessionProvider = (props) => {
    const [state, setState] = useState({
        persist: localStorage.getItem(PERSIST_KEY) || undefined,
        token: localStorage.getItem(AUTH_TOKEN_KEY) || undefined,
    });
    useEffect(() => {
        const persist = localStorage.getItem(PERSIST_KEY);
        if (persist === 'true')
            return;
        addSessionKey();
    }, []);
    useEffect(() => {
        const handleTabClose = (event) => {
            const persist = localStorage.getItem(PERSIST_KEY);
            if (persist === 'true')
                return;
            const sessionKey = sessionStorage.getItem(SESSION_KEY);
            const sessionKeys = JSON.parse(localStorage.getItem(SESSION_KEYS) || '[]');
            const remainingKeys = sessionKeys.filter((x) => x !== sessionKey);
            if (remainingKeys.length === 0) {
                setState({ token: undefined, persist: undefined });
                clearTokenFromStorage();
            }
            localStorage.setItem(SESSION_KEYS, JSON.stringify(remainingKeys));
        };
        window.addEventListener('beforeunload', handleTabClose);
        return () => {
            window.removeEventListener('beforeunload', handleTabClose);
        };
    }, []);
    useEffect(() => {
        const handleTabFocus = (event) => {
            const token = localStorage.getItem(AUTH_TOKEN_KEY);
            const persist = localStorage.getItem(PERSIST_KEY);
            if (!token) {
                setState({ token: undefined, persist: undefined });
            }
            else {
                setState({ token: token, persist: persist || 'false' });
                if (persist !== 'true') {
                    addSessionKey();
                }
            }
        };
        window.addEventListener('focus', handleTabFocus);
        return () => {
            window.removeEventListener('focus', handleTabFocus);
        };
    }, []);
    return React.createElement(Provider, { value: { state, setState } }, props.children);
};
const useSession = () => {
    const sessionContext = useContext(store);
    // Memoized State
    const isLoggedIn = useMemo(() => {
        var _a, _b;
        return !!((_b = (_a = sessionContext === null || sessionContext === void 0 ? void 0 : sessionContext.state) === null || _a === void 0 ? void 0 : _a.token) === null || _b === void 0 ? void 0 : _b.length);
    }, [sessionContext.state]);
    const decodedTokenData = useMemo(() => {
        var _a, _b, _c;
        return !!((_b = (_a = sessionContext === null || sessionContext === void 0 ? void 0 : sessionContext.state) === null || _a === void 0 ? void 0 : _a.token) === null || _b === void 0 ? void 0 : _b.length) ? jwtDecode((_c = sessionContext === null || sessionContext === void 0 ? void 0 : sessionContext.state) === null || _c === void 0 ? void 0 : _c.token) : undefined;
    }, [sessionContext.state]);
    // Methods
    const getClaim = (name) => {
        return decodedTokenData && decodedTokenData[name] && decodedTokenData[name].toString();
    };
    const logIn = (token, persist, successHandler) => {
        if (isLoggedIn)
            return;
        clearTokenFromStorage();
        sessionContext.setState({
            persist: persist ? 'true' : 'false',
            token: token,
        });
        localStorage.setItem(AUTH_TOKEN_KEY, token);
        localStorage.setItem(PERSIST_KEY, persist ? 'true' : 'false');
        if (!persist) {
            addSessionKey();
        }
        successHandler();
    };
    const logOut = (successHandler) => {
        sessionContext.setState({ token: undefined, persist: undefined });
        clearTokenFromStorage();
        successHandler();
    };
    return {
        getClaim: getClaim,
        token: sessionContext.state.token,
        isLoggedIn: isLoggedIn || false,
        logIn,
        logOut,
    };
};
export { store as SessionStore, SessionProvider, useSession };
//# sourceMappingURL=index.js.map